import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { isMagCapturePath } from "@/constants/mag-capture-routes";
import { flushMag, isMagLoaded } from "@/lib/mag";

/**
 * Keeps the Magig SDK out of the parts of the app it is not allowed in.
 *
 * index.html only injects the SDK where isMagCapturePath allows it, but this
 * is a single-document SPA: a visitor who lands on /register or /onboarding
 * with the SDK loaded and then client-side navigates to /dashboard is still on
 * that same document, and auto-capture would follow them in — reporting in-app
 * URLs and click text to a third-party processor. Scoping the tag is only half
 * the boundary; a full page load is the other half, because the SDK exposes no
 * way to stop it once running (data-disable is read at load, and there is no
 * teardown API).
 *
 * So: on the first navigation OUT of the allowed set with the SDK live, flush
 * what is pending and reload. index.html then declines to inject, and the rest
 * of the session runs clean.
 */
const RELOADED_KEY = "fiscalo:mag-scope-reloaded";

/** Storage access throws in some privacy modes; a failed read must not blank the app. */
function readFlag(): boolean {
  try {
    return sessionStorage.getItem(RELOADED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeFlag(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(RELOADED_KEY, "1");
    else sessionStorage.removeItem(RELOADED_KEY);
  } catch {
    /* ignore */
  }
}

export function MagScopeGuard(): null {
  const location = useLocation();

  useEffect(() => {
    if (isMagCapturePath(location.pathname)) {
      // Still somewhere the SDK is allowed: re-arm, so a later move into the
      // app proper gets its boundary.
      writeFlag(false);
      return;
    }
    if (!isMagLoaded()) return;
    // Belt and braces against a reload loop. If this file's route list and
    // index.html's copy ever disagree about a path, the honest failure is one
    // wasted reload — not an unloadable app.
    if (readFlag()) return;

    writeFlag(true);
    let done = false;
    const reload = (): void => {
      if (done) return;
      done = true;
      window.location.replace(location.pathname + location.search);
    };
    // Flush first so a just-tracked signup is not lost, but never let a
    // hanging request strand the user mid-flow.
    void flushMag().then(reload, reload);
    const timer = window.setTimeout(reload, 1500);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);

  return null;
}
